import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronDown } from 'lucide-react'
import { posGuard } from '@/api/client'
import { PosStaffScreen, initials } from './PosStaffScreen'
import { approveStaffAction, fetchStaffStatus, loginStaff, logoutStaff } from './service'
import type { PosStaffAction, PosStaffLogin, PosStaffPerson, PosStaffStatus } from './types'

/** Time to hand back change before "Nach jeder Bestellung" locks. Any input restarts it. */
const AFTER_ORDER_MS = 15_000
const POLL_MS = 30_000
interface Stored { token: string; person: PosStaffPerson; activeAt: number }
const storageKey = (restaurantId: number) => `pos-staff:${restaurantId}`
function read(restaurantId: number): Stored | null {
  try { return JSON.parse(sessionStorage.getItem(storageKey(restaurantId)) ?? 'null') as Stored | null } catch { return null }
}
function store(restaurantId: number, value: Stored | null) {
  try {
    if (value) sessionStorage.setItem(storageKey(restaurantId), JSON.stringify(value))
    else sessionStorage.removeItem(storageKey(restaurantId))
  } catch { /* A private tab only loses the login on reload. */ }
}
const amountCents = (body: unknown): number | undefined => {
  try {
    const value = (typeof body === 'string' ? JSON.parse(body) : body) as { expectedAmountCents?: number; amountCents?: number } | null
    return value?.expectedAmountCents ?? value?.amountCents
  } catch { return undefined }
}

export interface OpenTicket { totalText: string | null }
export interface PosStaffValue {
  status: PosStaffStatus | undefined
  unavailable: boolean
  person: PosStaffPerson | null
  signIn: (login: PosStaffLogin) => void
  lock: () => void
  orderFinished: () => void
  setTicket: (ticket: OpenTicket | null) => void
  setHold: (hold: boolean) => void
}
const PosStaffContext = createContext<PosStaffValue | null>(null)
/** Null outside the Kasse and for restaurants without POS, so callers simply skip all of this. */
export const usePosStaff = () => useContext(PosStaffContext)

interface Waiter { resolve: (headers: Record<string, string>) => void; reject: () => void }
interface Approval extends Waiter { action: PosStaffAction; amountCents?: number }

export interface PosStaffProviderProps {
  restaurantId: number
  /** False for restaurants without POS: nothing is fetched and usePosStaff stays null. */
  active?: boolean
  /** The visible screen needs a login, as Neue Bestellung and Kasse do. The order overview never does. */
  required: boolean
  onLeave: () => void
  restaurantName?: string
  children: ReactNode
}

export function PosStaffProvider({ restaurantId, active = true, required, onLeave, restaurantName, children }: PosStaffProviderProps) {
  const { t, i18n } = useTranslation('posOrdering')
  const queryClient = useQueryClient()
  const [person, setPerson] = useState<PosStaffPerson | null>(() => {
    const saved = read(restaurantId)
    posGuard.token = saved?.token ?? null
    return saved?.person ?? null
  })
  const last = useRef(person)
  const [ticket, setTicket] = useState<OpenTicket | null>(null)
  const [hold, setHold] = useState(false)
  const [asking, setAsking] = useState(false)
  const [approval, setApproval] = useState<Approval | null>(null)
  const waiters = useRef<Waiter[]>([])
  const status = useQuery({ queryKey: ['pos-staff', restaurantId], queryFn: ({ signal }) => fetchStaffStatus(restaurantId, signal),
    enabled: active, refetchInterval: POLL_MS, retry: false })
  const data = active ? status.data : undefined
  const enabled = data?.enabled === true

  const forget = useCallback(() => {
    posGuard.token = null
    store(restaurantId, null)
    setPerson(null)
  }, [restaurantId])
  const signIn = useCallback((login: PosStaffLogin) => {
    posGuard.token = login.token
    store(restaurantId, { token: login.token, person: login.staff, activeAt: Date.now() })
    last.current = login.staff
    setPerson(login.staff)
    setAsking(false)
    waiters.current.splice(0).forEach(waiter => waiter.resolve({}))
    void queryClient.invalidateQueries({ queryKey: ['pos-staff', restaurantId] })
  }, [queryClient, restaurantId])
  const lock = useCallback(() => {
    void logoutStaff(restaurantId).catch(() => undefined).finally(forget)
  }, [forget, restaurantId])

  useEffect(() => {
    // Also set while rendering, for the first requests; a remount in the same commit runs the old cleanup after that render.
    posGuard.token = read(restaurantId)?.token ?? null
    posGuard.onDenied = (code, action, body) => new Promise((resolve, reject) => {
      if (code === 'staff_login_required') { forget(); waiters.current.push({ resolve, reject }); setAsking(true) }
      else setApproval({ action: action as PosStaffAction, amountCents: amountCents(body), resolve, reject })
    })
    return () => { posGuard.onDenied = null; posGuard.token = null }
  }, [forget, restaurantId])
  // The server ends logins too: after the Tagesabschluss, on deactivation, when the feature is switched off.
  const current = data?.current?.id
  useEffect(() => { if (data && !current && posGuard.token) forget() }, [current, data, forget])

  const lockAfter = useRef<number | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const minutes = Number(data?.lockMode)
  useEffect(() => {
    if (!enabled || !person) return
    lockAfter.current = minutes > 0 ? minutes * 60_000 : null
    const saved = read(restaurantId)
    if (lockAfter.current && saved && Date.now() - saved.activeAt > lockAfter.current) { lock(); return }
    const touch = () => {
      clearTimeout(timer.current)
      if (lockAfter.current) timer.current = setTimeout(lock, lockAfter.current)
      const now = read(restaurantId)
      if (now) store(restaurantId, { ...now, activeAt: Date.now() })
    }
    touch()
    window.addEventListener('pointerdown', touch, { passive: true })
    window.addEventListener('keydown', touch)
    return () => { clearTimeout(timer.current); window.removeEventListener('pointerdown', touch); window.removeEventListener('keydown', touch) }
  }, [enabled, lock, minutes, person, restaurantId])
  const lockMode = data?.lockMode
  const orderFinished = useCallback(() => {
    if (lockMode !== 'order') return
    lockAfter.current = AFTER_ORDER_MS
    clearTimeout(timer.current)
    timer.current = setTimeout(lock, AFTER_ORDER_MS)
  }, [lock, lockMode])

  const unavailable = status.isError
  const value = useMemo<PosStaffValue | null>(() => active ? { status: data, unavailable, person: enabled ? person : null, signIn, lock, orderFinished, setTicket, setHold } : null,
    [active, data, enabled, lock, orderFinished, person, signIn, unavailable])
  const previous = last.current
  const people = data?.staff ?? []
  const loginOpen = Boolean(data) && (asking || (enabled && required && !person && !hold))
  function leave() {
    waiters.current.splice(0).forEach(waiter => waiter.reject())
    setAsking(false)
    if (required) onLeave()
  }
  return <PosStaffContext.Provider value={value}>
    {children}
    {loginOpen && !approval && <PosStaffScreen key="login" people={people} title={t('staff.selectPerson')} backLabel={t('staff.orders')} onBack={leave} brand={restaurantName}
      onSubmit={async (who, pin) => { signIn(await loginStaff(restaurantId, who.id, pin)) }}
      hint={who => ticket && previous?.id === who.id ? t('staff.openOrder') : undefined}
      note={who => ticket && previous && previous.id !== who.id ? t('staff.willPark', { name: previous.name }) + (ticket.totalText ? ` (${ticket.totalText})` : '') : undefined} />}
    {approval && <PosStaffScreen key="approval" people={people.filter(who => who.role === 'manager')} title={t('staff.selectManager')} backLabel={t('staff.cancel')} brand={restaurantName}
      onBack={() => { approval.reject(); setApproval(null) }}
      onSubmit={async (who, pin) => {
        const token = await approveStaffAction(restaurantId, approval.action, who.id, pin)
        approval.resolve({ 'X-Pos-Approval': token })
        setApproval(null)
      }}
      summary={<div className="space-y-1">
        <h2 className="font-menu text-[26px] font-normal leading-8 text-accent-foreground">{t('staff.approvalTitle')}</h2>
        <Line label={t('staff.approvalAction')} value={t(`staff.actions.${approval.action}`)} />
        {approval.amountCents !== undefined && <Line label={t('staff.amount')} value={(approval.amountCents / 100).toLocaleString(i18n.language, { style: 'currency', currency: 'EUR' })} />}
        {person && <Line label={t('staff.requestedBy')} value={person.name} />}
      </div>} />}
  </PosStaffContext.Provider>
}

function Line({ label, value }: { label: string; value: string }) {
  return <p className="flex items-baseline gap-2 border-t py-1.5 text-[17px] first:border-t-0"><span>{label}</span><span aria-hidden className="pos-leader" /><strong className="font-semibold">{value}</strong></p>
}

/** Who is logged in, and the one way to hand over: the name screen. */
export function PosStaffChip() {
  const { t } = useTranslation('posOrdering')
  const staff = usePosStaff()
  if (!staff?.person) return null
  return <button type="button" onClick={staff.lock} className="pos-print flex h-14 shrink-0 items-center gap-2.5 rounded-xl bg-secondary pe-3 ps-1.5 text-base font-semibold text-foreground transition-colors hover:bg-secondary/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
    <span aria-hidden className="flex h-11 w-11 items-center justify-center rounded-[9px] bg-foreground text-[15px] font-bold text-background">{initials(staff.person.name)}</span>
    <span className="hidden max-w-32 truncate sm:block">{staff.person.name}</span>
    <span className="sr-only">{t('staff.switch')}</span>
    <ChevronDown aria-hidden className="hidden h-[18px] w-[18px] sm:block" />
  </button>
}
